// Demo Mode Auth Context - Simulates logged-in state for preview
import React, { createContext, useContext, useState, useEffect } from 'react';

interface DemoUser {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string;
  tokenBalance: number;
  subscriptionTier: 'free' | 'basic' | 'pro';
}

interface DemoAuthContextType {
  user: DemoUser | null;
  isDemo: boolean;
  loginDemo: () => void;
  logoutDemo: () => void;
}

const DemoAuthContext = createContext<DemoAuthContextType | null>(null);

export function DemoAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<DemoUser | null>(null);
  const [isDemo, setIsDemo] = useState(false);

  useEffect(() => {
    // Check for demo mode in URL
    const params = new URLSearchParams(window.location.search);
    if (params.get('demo') === 'true') {
      loginDemo();
    }
  }, []);

  const loginDemo = () => {
    setUser({
      id: 'demo_user_123',
      email: 'demo@notlegaladvice.com',
      displayName: 'Demo User',
      avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=demo',
      tokenBalance: 2500,
      subscriptionTier: 'pro',
    });
    setIsDemo(true);
  };

  const logoutDemo = () => {
    setUser(null);
    setIsDemo(false);
    // Remove demo param from URL
    const params = new URLSearchParams(window.location.search);
    params.delete('demo');
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  };

  return (
    <DemoAuthContext.Provider value={{ user, isDemo, loginDemo, logoutDemo }}>
      {children}
    </DemoAuthContext.Provider>
  );
}

export function useDemoAuth() {
  const context = useContext(DemoAuthContext);
  if (!context) {
    throw new Error('useDemoAuth must be used within DemoAuthProvider');
  }
  return context;
}

// Demo user for direct import
export const DEMO_USER: DemoUser = {
  id: 'demo_user_123',
  email: 'demo@notlegaladvice.com',
  displayName: 'Demo User',
  avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=demo',
  tokenBalance: 2500,
  subscriptionTier: 'pro',
};
